import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ChevronRight, Pause, XCircle, RefreshCw, Edit2, Save, X, History, Wrench, Lock, Factory, AlertTriangle,
} from "lucide-react";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import ProgressStepper from "@/components/ProgressStepper";
import CylinderBlueprint from "@/components/CylinderBlueprint";
import CommercialLinkage from "@/components/CommercialLinkage";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import {
  useAuth, canHoldOrCancel, canViewCost, canViewRealizedCost, ROLE_STATUS_TARGETS,
} from "@/contexts/AuthContext";

// Client-side mirror of backend state machine
const STATE_MACHINE = {
  CREATED: ["DESIGN_IN_PROGRESS"],
  DESIGN_IN_PROGRESS: ["DESIGN_APPROVED"],
  DESIGN_APPROVED: ["IN_PRODUCTION"],
  IN_PRODUCTION: ["QUALITY_PENDING"],
  QUALITY_PENDING: ["QUALITY_PASSED", "QUALITY_FAILED"],
  QUALITY_PASSED: ["READY_FOR_DISPATCH"],
  QUALITY_FAILED: ["REWORK", "REJECTED_SCRAPPED"],
  REWORK: ["QUALITY_PENDING"],
  READY_FOR_DISPATCH: ["DISPATCHED"],
  DISPATCHED: [],
  REJECTED_SCRAPPED: [],
  ON_HOLD: ["CREATED", "DESIGN_IN_PROGRESS", "DESIGN_APPROVED", "IN_PRODUCTION", "QUALITY_PENDING", "QUALITY_PASSED", "REWORK", "READY_FOR_DISPATCH"],
  CANCELLED: [],
};

function allowedTargets(cyl, role) {
  const fromStatus = cyl.cylinder_status;
  const smAllowed = STATE_MACHINE[fromStatus] || [];
  const roleAllowed = ROLE_STATUS_TARGETS[role];
  if (roleAllowed === null) return smAllowed; // admins/plant_head see all valid transitions
  if (!Array.isArray(roleAllowed)) return [];
  return smAllowed.filter((s) => roleAllowed.includes(s));
}

export default function CylinderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [engineering, setEngineering] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSerial, setEditingSerial] = useState(false);
  const [serialDraft, setSerialDraft] = useState("");
  const [statusDialog, setStatusDialog] = useState(false);
  const [holdDialog, setHoldDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [target, setTarget] = useState("");
  const [remarks, setRemarks] = useState("");
  const [dispatchDate, setDispatchDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    const [d, h] = await Promise.all([
      api.get(`/cylinders/${id}`),
      api.get(`/cylinders/${id}/history`),
    ]);
    setData(d.data);
    setHistory(h.data);
    setSerialDraft(d.data.cylinder.serial_number || "");
    // Fetch engineering (revisions + block) — non-blocking; ignore failure so cylinder page always renders
    try {
      const eng = await api.get(`/engineering/${id}`);
      setEngineering(eng.data);
    } catch (_) {
      setEngineering(null);
    }
    // Fetch lifecycle block for the Manufacturing sections
    try {
      const lc = await api.get(`/lifecycle/${id}`);
      setLifecycle(lc.data);
    } catch (_) {
      setLifecycle(null);
    }
    setLoading(false);
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
      </div>
    );
  }

  const { cylinder, specification, cost, customer, plant, purchase_order, order_acceptance, inquiry, quotation } = data;
  const targets = allowedTargets(cylinder, user.role);
  const canChange = targets.length > 0;

  async function saveSerial() {
    setSaving(true);
    try {
      await api.patch(`/cylinders/${id}/serial`, { serial_number: serialDraft });
      toast.success("Serial number updated");
      setEditingSerial(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  async function submitStatusChange() {
    if (!target) return;
    if (target === "DISPATCHED" && !dispatchDate) {
      toast.error("Actual dispatch date is required for DISPATCHED");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/cylinders/${id}/status`, {
        to_status: target,
        remarks: remarks || null,
        actual_dispatch_date: target === "DISPATCHED" ? dispatchDate : null,
      });
      toast.success(`Status changed to ${STATUS_LABELS[target]}`);
      setStatusDialog(false);
      setTarget(""); setRemarks(""); setDispatchDate("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Status change failed");
    } finally {
      setSaving(false);
    }
  }

  async function submitHold() {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setSaving(true);
    try {
      await api.post(`/cylinders/${id}/hold`, { reason });
      toast.success("Cylinder placed on hold");
      setHoldDialog(false); setReason("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Hold failed");
    } finally {
      setSaving(false);
    }
  }

  async function submitCancel() {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setSaving(true);
    try {
      await api.post(`/cylinders/${id}/cancel`, { reason });
      toast.success("Cylinder cancelled");
      setCancelDialog(false); setReason("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed");
    } finally {
      setSaving(false);
    }
  }

  async function submitRebuild() {
    setSaving(true);
    try {
      const res = await api.post(`/cylinders/${id}/rebuild`);
      toast.success(`Rebuilt as ${res.data.cylinder_number}`);
      nav(`/cylinders/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rebuild failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="cylinder-detail-page" className="space-y-5 pb-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/cylinders" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Cylinders
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-900 font-mono">{cylinder.cylinder_number}</span>
      </div>

      {/* Header card */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="p-5 md:p-6 border-b border-slate-100">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">
                Digital Passport
              </div>
              <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900 break-all">
                {cylinder.cylinder_number}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Serial:
                </span>
                {editingSerial ? (
                  <div className="flex items-center gap-1">
                    <Input
                      data-testid="serial-input"
                      value={serialDraft}
                      onChange={(e) => setSerialDraft(e.target.value)}
                      className="h-7 w-40 font-mono text-xs"
                      placeholder="SN-..."
                    />
                    <Button size="icon" variant="ghost" data-testid="serial-save" onClick={saveSerial} disabled={saving} className="h-7 w-7">
                      <Save className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => { setEditingSerial(false); setSerialDraft(cylinder.serial_number || ""); }} className="h-7 w-7">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-sm font-semibold text-slate-800">
                      {cylinder.serial_number || <span className="text-slate-400 italic font-normal">not set</span>}
                    </span>
                    {cylinder.cylinder_status !== "DISPATCHED" && (
                      <Button size="icon" variant="ghost" data-testid="serial-edit" onClick={() => setEditingSerial(true)} className="h-6 w-6">
                        <Edit2 className="w-3 h-3 text-slate-500" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col md:items-end gap-3">
              <StatusBadge status={cylinder.cylinder_status} className="text-xs px-3 py-1" />
              <div className="flex flex-wrap gap-2">
                {canChange && (
                  <Button
                    data-testid="change-status-btn"
                    size="sm"
                    onClick={() => setStatusDialog(true)}
                    className="bg-blue-900 hover:bg-blue-800 text-white h-8"
                  >
                    Change Status
                  </Button>
                )}
                {canHoldOrCancel(user.role) && !["DISPATCHED", "CANCELLED", "REJECTED_SCRAPPED", "ON_HOLD"].includes(cylinder.cylinder_status) && (
                  <Button
                    data-testid="hold-btn"
                    size="sm"
                    variant="outline"
                    onClick={() => setHoldDialog(true)}
                    className="h-8"
                  >
                    <Pause className="w-3.5 h-3.5 mr-1" />
                    Hold
                  </Button>
                )}
                {canHoldOrCancel(user.role) && !["DISPATCHED", "CANCELLED", "REJECTED_SCRAPPED"].includes(cylinder.cylinder_status) && (
                  <Button
                    data-testid="cancel-btn"
                    size="sm"
                    variant="outline"
                    onClick={() => setCancelDialog(true)}
                    className="h-8 border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" />
                    Cancel
                  </Button>
                )}
                {cylinder.cylinder_status === "REJECTED_SCRAPPED" && (
                  <Button
                    data-testid="rebuild-btn"
                    size="sm"
                    onClick={submitRebuild}
                    disabled={saving}
                    className="h-8 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    Rebuild
                  </Button>
                )}
                <AdminDeleteButton
                  endpoint={`/cylinders/${cylinder.id}`}
                  entityName="Cylinder"
                  entityNumber={cylinder.cylinder_number}
                  redirectAfter="/cylinders"
                  allowCascade={false}
                />
              </div>
            </div>
          </div>

          {(cylinder.hold_reason || cylinder.cancelled_reason) && (
            <div className="mt-4 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2">
              <strong>{cylinder.hold_reason ? "Hold reason:" : "Cancellation reason:"}</strong>{" "}
              {cylinder.hold_reason || cylinder.cancelled_reason}
            </div>
          )}
          {cylinder.rebuilt_from_cylinder_id && (
            <div className="mt-3 text-xs bg-blue-50 border border-blue-200 text-blue-900 rounded px-3 py-2">
              Rebuilt from a previously scrapped cylinder (parent id: <span className="font-mono">{cylinder.rebuilt_from_cylinder_id.slice(0, 8)}…</span>)
            </div>
          )}

          {/* Current Stage + Owner + Department */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Current Stage</div>
              <div className="mt-1 text-sm font-bold text-slate-900" data-testid="current-stage">
                {cylinder.current_stage || cylinder.cylinder_status}
              </div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Responsible Department</div>
              <div className="mt-1 text-sm font-bold text-slate-900" data-testid="responsible-dept">
                {cylinder.responsible_department || "—"}
              </div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Current Owner</div>
              <div className="mt-1 text-sm font-bold text-slate-900" data-testid="current-owner">
                {cylinder.current_owner_name || <span className="text-slate-400 font-normal italic">Unassigned</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 md:px-6 py-5 bg-slate-50/70 border-b border-slate-100">
          <ProgressStepper currentStatus={cylinder.cylinder_status} />
        </div>
      </Card>

      {/* Commercial Link Manager — top-level, prominent (post-import backfill) */}
      <CommercialLinkage cylinderId={cylinder.id} onLinkageChange={refresh} />

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Lineage — Commercial Information */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Commercial Lineage</div>
            <div className="text-xs text-slate-500">Immutable ancestry from inquiry to release</div>
          </div>
          <div className="p-5 space-y-3 text-sm">
            <LineageItem label="Customer" value={customer?.name} />
            <LineageItem label="Plant" value={plant?.name} />
            {inquiry && <LineageItem label="Inquiry" value={inquiry.inquiry_number} mono />}
            {inquiry?.customer_pr_number && <LineageItem label="Customer PR/RFQ" value={inquiry.customer_pr_number} mono />}
            {quotation && <LineageItem label={`Quotation (Rev ${quotation.current_revision || 1})`} value={quotation.quotation_number} mono />}
            <LineageItem label="Purchase Order" value={purchase_order?.po_number} mono />
            <LineageItem label="Order Acceptance" value={order_acceptance?.oa_number} mono />
            {cylinder.workflow && cylinder.workflow !== "New Manufacturing" && (
              <LineageItem label="Workflow" value={cylinder.workflow} />
            )}
            {cylinder.delivery_challan_number && (
              <>
                <LineageItem label="Delivery Challan (DC)" value={
                  cylinder.delivery_challan_id ? (
                    <Link to={`/receiving/${cylinder.delivery_challan_id}`}
                          data-testid="passport-dc-link"
                          className="text-amber-800 hover:underline font-mono bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      {cylinder.delivery_challan_number}
                    </Link>
                  ) : (
                    <span data-testid="passport-dc-badge" className="text-amber-800 font-mono bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      {cylinder.delivery_challan_number}
                    </span>
                  )
                } />
                {cylinder.delivery_challan_date && <LineageItem label="DC Date" value={cylinder.delivery_challan_date} mono />}
                {cylinder.cylinder_received_date && <LineageItem label="Cylinder Received" value={cylinder.cylinder_received_date} mono />}
              </>
            )}
            <LineageItem label="Created" value={new Date(cylinder.created_at).toLocaleString()} />
            {cylinder.planned_dispatch_date && (
              <LineageItem label="Planned Dispatch" value={cylinder.planned_dispatch_date} mono />
            )}
            {cylinder.actual_dispatch_date && (
              <LineageItem label="Actual Dispatch" value={cylinder.actual_dispatch_date} mono />
            )}
          </div>
        </Card>

        {/* Specification */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Specification</div>
              <div className="text-xs text-slate-500">Point-in-time snapshot at OA</div>
            </div>
            <button
              type="button"
              data-testid="passport-print-certificate"
              onClick={() => window.print()}
              className="text-[11px] font-semibold uppercase tracking-wider text-white rounded px-2.5 py-1"
              style={{ backgroundColor: "#C62828" }}
            >
              Print Certificate
            </button>
          </div>
          {/* Auto-generated blueprint watermark from actual spec */}
          {specification?.bore_diameter_mm && (
            <div className="border-b border-slate-100" style={{ backgroundColor: "#F7F8FA" }}>
              <CylinderBlueprint
                variant="certificate"
                bore={specification.bore_diameter_mm}
                rod={specification.rod_diameter_mm}
                stroke={specification.stroke_length_mm}
                cylinderNumber={cylinder.cylinder_number}
                drawingNumber={engineering?.engineering?.general_assembly_drawing_number || engineering?.engineering?.drawing_number}
                customerName={customer?.name}
                plantName={plant?.name}
                data-testid="passport-blueprint"
                className="w-full h-auto"
              />
            </div>
          )}
          <div className="p-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <Spec label="Bore Ø" value={`${specification?.bore_diameter_mm} mm`} />
            <Spec label="Rod Ø" value={`${specification?.rod_diameter_mm} mm`} />
            <Spec label="Stroke" value={`${specification?.stroke_length_mm} mm`} />
            <Spec label="Closed Len." value={`${specification?.closed_length_mm} mm`} />
            <Spec label="Pressure" value={`${specification?.operating_pressure_bar} bar`} />
            <Spec label="Mounting" value={specification?.mounting_type} />
            <Spec label="Seal Kit" value={specification?.seal_kit_type} full />
            {specification?.custom_notes && (
              <div className="col-span-2 text-xs text-slate-600 border-t border-slate-100 pt-2">
                {specification.custom_notes}
              </div>
            )}
          </div>
        </Card>

        {/* Cost card */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Cost & Margin</div>
              <div className="text-xs text-slate-500">
                {canViewCost(user.role) ? "Role-gated financial data" : "Restricted to Finance / Plant Head"}
              </div>
            </div>
            {canViewCost(user.role) && (
              <span className="text-[9px] uppercase tracking-wider bg-blue-900/5 text-blue-900 border border-blue-200 px-1.5 py-0.5 rounded font-semibold">
                Confidential
              </span>
            )}
          </div>
          {!canViewCost(user.role) ? (
            <div className="p-8 text-center text-xs text-slate-400 border border-dashed m-4 rounded">
              🔒 You don't have permission to view cost or margin data.
            </div>
          ) : cost ? (
            <div className="p-5 space-y-2.5 text-sm">
              <CostRow label="Quoted Price" value={cost.quoted_price} />
              <CostRow label="Material" value={cost.material_cost_actual} muted />
              <CostRow label="Labor" value={cost.labor_cost_actual} muted />
              <CostRow label="Machine Overhead" value={cost.machine_overhead_cost} muted />
              <CostRow label="Subcontract" value={cost.subcontract_cost_actual} muted />
              <div className="pt-2 border-t border-slate-100">
                <CostRow label="Provisional Margin" value={cost.provisional_margin} highlight />
                {canViewRealizedCost(user.role) && cost.realized_price != null && (
                  <>
                    <CostRow label="Realized Price" value={cost.realized_price} />
                    <CostRow label="Realized Margin" value={cost.realized_margin} highlight />
                    {cost.margin_locked && (
                      <div className="mt-1 text-[10px] text-emerald-700 uppercase tracking-wider font-semibold">
                        ● Margin locked after payment
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : null}
        </Card>
      </div>

      {/* Engineering Section — Module 4 */}
      <Card data-testid="passport-engineering" className="border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-red-600" />
            <div>
              <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Engineering</div>
              <div className="text-xs text-slate-500">Design status, drawing information, lead time & revisions</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {engineering?.engineering?.status && (() => {
              const s = engineering.engineering.status;
              const cls = s === "Released" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : s === "Ongoing" ? "bg-amber-50 text-amber-800 border-amber-200"
                : s === "Revision Required" ? "bg-rose-50 text-rose-700 border-rose-200"
                : s === "Rejected" ? "bg-red-50 text-red-700 border-red-200"
                : "bg-slate-100 text-slate-700 border-slate-200";
              return (
                <span data-testid="passport-eng-status"
                      className={`inline-flex items-center gap-1.5 border px-2 py-0.5 rounded text-[11px] font-semibold uppercase font-mono ${cls}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{s}
                </span>
              );
            })()}
            {engineering?.engineering?.status === "Released" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                <Lock className="w-3 h-3" /> Locked
              </span>
            )}
            <Link to={`/engineering/${cylinder.id}`}
                  data-testid="passport-open-engineering"
                  className="text-xs text-blue-900 hover:text-blue-700 font-semibold">
              Open Engineering →
            </Link>
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          <Spec label="Cylinder Number" value={`#${cylinder.cylinder_number}`} />
          <Spec label="GA Drawing #" value={engineering?.engineering?.general_assembly_drawing_number || engineering?.engineering?.drawing_number || "—"} />
          <Spec label="Part Drawing #" value={engineering?.engineering?.part_drawing_number || "—"} />
          <Spec label="Current Revision" value={engineering?.engineering?.current_revision || "—"} />
          <Spec label="Design Engineer" value={engineering?.engineering?.design_engineer_name || "Unassigned"} />
          <Spec label="Assigned Date" value={engineering?.engineering?.assigned_date ? engineering.engineering.assigned_date.slice(0, 10) : "—"} />
          <Spec label="Released Date" value={engineering?.engineering?.released_date || "—"} />
          <Spec label="Design Lead Time"
                value={engineering?.engineering?.design_lead_time_days != null
                  ? `${engineering.engineering.design_lead_time_days} days`
                  : "—"} />
          <Spec label="Approved By" value={engineering?.engineering?.approved_by_name || "—"} />
          <Spec label="Approval Date"
                value={engineering?.engineering?.approved_at ? new Date(engineering.engineering.approved_at).toLocaleString() : "—"} />
        </div>
        {/* Revision History */}
        <div className="px-5 pb-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Revision History</div>
          {!engineering?.revisions?.length ? (
            <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded p-3 text-center">
              No revisions recorded yet.
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="passport-revision-history">
              {engineering.revisions.map((r) => (
                <div key={r.id}
                     className={`px-3 py-2 border rounded flex items-center justify-between text-xs ${
                       r.status === "Released" ? "border-emerald-200 bg-emerald-50/40"
                       : r.status === "Current" ? "border-blue-300 bg-blue-50/40"
                       : "border-slate-200 bg-white"}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-slate-900">{r.drawing_number}</span>
                    <span className="font-mono font-semibold text-blue-900">{r.revision_number}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      r.status === "Released" ? "bg-emerald-100 text-emerald-800"
                      : r.status === "Current" ? "bg-blue-100 text-blue-800"
                      : "bg-slate-100 text-slate-600"}`}>
                      {r.status}
                    </span>
                    {r.drawing_description && <span className="text-slate-600 italic">{r.drawing_description}</span>}
                  </div>
                  <span className="font-mono text-slate-500">
                    {r.created_at?.slice(0, 10)} · {r.created_by_name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Manufacturing Lifecycle — Module 5 */}
      {lifecycle && (
        <Card data-testid="passport-lifecycle" className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Factory className="w-4 h-4 text-red-600" />
              <div>
                <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Manufacturing Lifecycle</div>
                <div className="text-xs text-slate-500">
                  {lifecycle.lifecycle.manufacturing_type === "Reconditioning" ? "Reconditioning" : "New Manufacturing"} workflow — Module 5
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Current Phase</span>
              <span data-testid="passport-lc-phase" className="font-mono text-xs font-bold text-blue-900 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">
                {lifecycle.current_phase}
              </span>
              {lifecycle.delays.length > 0 && (
                <span data-testid="passport-lc-delayed" className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                  <AlertTriangle className="w-3 h-3" /> {lifecycle.delays.length} delayed
                </span>
              )}
              <Link to={`/lifecycle/${cylinder.id}`}
                    data-testid="passport-open-lifecycle"
                    className="text-xs text-blue-900 hover:text-blue-700 font-semibold">
                Open Lifecycle →
              </Link>
            </div>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              {[
                ["Design", lifecycle.engineering.status, "Engineering", lifecycle.engineering.approved_by_name, lifecycle.engineering.released_date],
                ["BOM", lifecycle.lifecycle.bom.status, "Design", lifecycle.lifecycle.bom.prepared_by_name, lifecycle.lifecycle.bom.completion_date],
                ["Raw Material", lifecycle.lifecycle.procurement.raw_material.status, "Procurement",
                  lifecycle.lifecycle.procurement.raw_material.updated_by_name,
                  lifecycle.lifecycle.procurement.raw_material.completed_at?.slice(0, 10)],
                ["Tube Machining", lifecycle.lifecycle.machining.tube.status, "Production",
                  lifecycle.lifecycle.machining.tube.updated_by_name,
                  lifecycle.lifecycle.machining.tube.completed_at?.slice(0, 10)],
                ["Rod Machining", lifecycle.lifecycle.machining.piston_rod.status, "Production",
                  lifecycle.lifecycle.machining.piston_rod.updated_by_name,
                  lifecycle.lifecycle.machining.piston_rod.completed_at?.slice(0, 10)],
                ["Assembly", lifecycle.lifecycle.assembly.status, "Production",
                  lifecycle.lifecycle.assembly.engineer_name,
                  lifecycle.lifecycle.assembly.completion_date],
                ["Testing", lifecycle.lifecycle.testing.status, "Quality",
                  lifecycle.lifecycle.testing.engineer_name,
                  lifecycle.lifecycle.testing.test_date],
                ["FIR", lifecycle.lifecycle.fir.done ? "Completed" : "Pending", "Quality",
                  lifecycle.lifecycle.fir.approved_by_name,
                  lifecycle.lifecycle.fir.date],
                // Extra tracking (added in this iteration)
                ["Design Review", lifecycle.lifecycle.design_review?.review_meeting_done ? "Completed" : "Pending", "Design",
                  lifecycle.lifecycle.design_review?.reviewed_by_name,
                  lifecycle.lifecycle.design_review?.review_date],
                ["Material (Purchase)", lifecycle.lifecycle.purchase_tracking?.material_status || "Pending", "Purchase",
                  lifecycle.lifecycle.purchase_tracking?.updated_by_name,
                  lifecycle.lifecycle.purchase_tracking?.updated_at?.slice(0, 10)],
                ["Subcontract", lifecycle.lifecycle.subcontract_tracking?.status || "Not Required", "Subcontract",
                  lifecycle.lifecycle.subcontract_tracking?.updated_by_name,
                  lifecycle.lifecycle.subcontract_tracking?.updated_at?.slice(0, 10)],
                ["Cyl. Readiness", lifecycle.lifecycle.cylinder_readiness?.ready_for_assembly ? "Completed" : "Pending", "Quality",
                  lifecycle.lifecycle.cylinder_readiness?.verified_by_name,
                  lifecycle.lifecycle.cylinder_readiness?.verification_date],
              ].map(([label, status, dept, emp, date]) => {
                const s = status || "Pending";
                const clsMap = {
                  Pending: "bg-slate-100 text-slate-700 border-slate-200",
                  "In Progress": "bg-amber-50 text-amber-800 border-amber-200",
                  "Under Machining": "bg-amber-50 text-amber-800 border-amber-200",
                  Ongoing: "bg-amber-50 text-amber-800 border-amber-200",
                  Issued: "bg-blue-50 text-blue-800 border-blue-200",
                  "Partially Received": "bg-amber-50 text-amber-800 border-amber-200",
                  Sent: "bg-blue-50 text-blue-800 border-blue-200",
                  "Under Process": "bg-amber-50 text-amber-800 border-amber-200",
                  Received: "bg-blue-50 text-blue-800 border-blue-200",
                  "Not Required": "bg-slate-100 text-slate-500 border-slate-200",
                  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
                  Released: "bg-emerald-50 text-emerald-700 border-emerald-200",
                  "Revision Required": "bg-rose-50 text-rose-700 border-rose-200",
                  Rejected: "bg-red-50 text-red-700 border-red-200",
                };
                const cls = clsMap[s] || "bg-slate-100 text-slate-700 border-slate-200";
                return (
                  <div key={label} className="border border-slate-200 rounded p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                    <div className="mt-1">
                      <span className={`inline-flex items-center border px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase font-mono ${cls}`}>{s}</span>
                    </div>
                    <div className="mt-2 text-[10px] text-slate-500">
                      <div>Dept: <span className="text-slate-700">{dept}</span></div>
                      <div>By: <span className="text-slate-700">{emp || "—"}</span></div>
                      <div>Date: <span className="font-mono text-slate-700">{date || "—"}</span></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Dispatch card */}
            <div className="mt-4 border border-slate-200 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Dispatch Details</div>
                <span data-testid="passport-lc-dispatch-chip"
                      className={`inline-flex items-center border px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase font-mono ${
                        lifecycle.lifecycle.dispatch.status === "Dispatched" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : lifecycle.lifecycle.dispatch.status === "Ready" ? "bg-teal-50 text-teal-800 border-teal-200"
                        : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                  {lifecycle.lifecycle.dispatch.status}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><span className="text-slate-500">Transporter: </span><span className="text-slate-800">{lifecycle.lifecycle.dispatch.transporter || "—"}</span></div>
                <div><span className="text-slate-500">Date: </span><span className="font-mono text-slate-800">{lifecycle.lifecycle.dispatch.dispatch_date || "—"}</span></div>
                <div><span className="text-slate-500">Invoice: </span><span className="font-mono text-slate-800">{lifecycle.lifecycle.dispatch.invoice_number || "—"}</span></div>
                <div><span className="text-slate-500">Value: </span><span className="font-mono text-slate-800">{lifecycle.lifecycle.dispatch.invoice_value != null ? `₹${lifecycle.lifecycle.dispatch.invoice_value.toLocaleString("en-IN")}` : "—"}</span></div>
                <div><span className="text-slate-500">Vehicle #: </span><span className="font-mono text-slate-800">{lifecycle.lifecycle.dispatch.vehicle_number || "—"}</span></div>
                <div><span className="text-slate-500">LR #: </span><span className="font-mono text-slate-800">{lifecycle.lifecycle.dispatch.lr_number || "—"}</span></div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* History timeline */}
      <Card className="border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Status History</div>
          <div className="text-xs text-slate-500 ml-2">Every state transition is logged</div>
        </div>
        <div className="p-5">
          {history.length === 0 ? (
            <div className="text-xs text-slate-400 py-4">No history yet.</div>
          ) : (
            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-5">
              {history.map((h, idx) => (
                <li key={h.id} className="pl-5 relative" data-testid={`history-item-${idx}`}>
                  <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${
                    idx === 0 ? "bg-blue-900" : "bg-emerald-500"
                  }`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    {h.from_status && (
                      <>
                        <StatusBadge status={h.from_status} />
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                      </>
                    )}
                    <StatusBadge status={h.to_status} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {new Date(h.changed_at).toLocaleString()} · by <span className="font-medium text-slate-700">{h.changed_by_name || "system"}</span>
                  </div>
                  {h.remarks && (
                    <div className="mt-1 text-xs text-slate-600 italic">"{h.remarks}"</div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </Card>

      {/* Documents Placeholder */}
      <Card className="border-slate-200 border-dashed">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Documents</div>
          <span className="text-[9px] uppercase tracking-widest bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-semibold">
            Placeholder
          </span>
        </div>
        <div className="p-5">
          {(cylinder.documents || []).length === 0 ? (
            <div className="text-center py-6 text-xs text-slate-400">
              No documents attached yet.<br />
              <span className="text-[10px]">Drawings, inspection reports, and dispatch documents will appear here as production progresses.</span>
            </div>
          ) : (
            <div className="text-xs text-slate-600">{cylinder.documents.length} document(s) attached.</div>
          )}
        </div>
      </Card>

      {/* Audit Trail — every link / unlink / edit / delete is recorded here */}
      <AuditTrailPanel cylinderId={cylinder.id} />

      {/* Status change dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent data-testid="status-change-dialog">
          <DialogHeader>
            <DialogTitle>Change Cylinder Status</DialogTitle>
            <DialogDescription>
              Current: <StatusBadge status={cylinder.cylinder_status} className="ml-1 inline-flex" />
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">New status</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="target-status-select" className="mt-1">
                  <SelectValue placeholder="Choose target status" />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`target-option-${s}`}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {target === "DISPATCHED" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Actual dispatch date *</Label>
                <Input
                  data-testid="dispatch-date"
                  type="date"
                  value={dispatchDate}
                  onChange={(e) => setDispatchDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Remarks (optional)</Label>
              <Textarea
                data-testid="status-remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="mt-1"
                rows={3}
                placeholder="Add context, batch number, machine no., etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>Cancel</Button>
            <Button data-testid="status-confirm" onClick={submitStatusChange} disabled={saving || !target} className="bg-blue-900 hover:bg-blue-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold dialog */}
      <Dialog open={holdDialog} onOpenChange={setHoldDialog}>
        <DialogContent data-testid="hold-dialog">
          <DialogHeader>
            <DialogTitle>Put Cylinder On Hold</DialogTitle>
            <DialogDescription>A hold reason is mandatory and will be logged.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Reason *</Label>
            <Textarea
              data-testid="hold-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1"
              placeholder="e.g. Awaiting customer clarification on rod thread spec"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoldDialog(false)}>Cancel</Button>
            <Button data-testid="hold-confirm" onClick={submitHold} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Place On Hold"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Cylinder</DialogTitle>
            <DialogDescription>
              The cylinder number will be preserved as CANCELLED for audit. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Cancellation reason *</Label>
            <Textarea
              data-testid="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1"
              placeholder="e.g. PO amendment - line item removed"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>Back</Button>
            <Button data-testid="cancel-confirm" onClick={submitCancel} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LineageItem({ label, value, mono }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      <span className={`text-right text-sm text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>
        {value || <span className="text-slate-300">—</span>}
      </span>
    </div>
  );
}

function Spec({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-900 font-semibold mt-0.5 font-mono">
        {value || <span className="text-slate-300">—</span>}
      </div>
    </div>
  );
}

function CostRow({ label, value, muted, highlight }) {
  const displayValue = value != null
    ? `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
    : "—";
  return (
    <div className="flex justify-between items-baseline">
      <span className={`text-xs ${muted ? "text-slate-500" : "text-slate-700"} ${highlight ? "font-semibold text-slate-900 uppercase tracking-wider" : ""}`}>
        {label}
      </span>
      <span className={`font-mono ${highlight ? "text-base font-bold text-emerald-700" : "text-sm text-slate-900"}`}>
        {displayValue}
      </span>
    </div>
  );
}
